import { supabase } from './supabase';

async function invoke(fnName, body) {
  const { data, error } = await supabase.functions.invoke(fnName, { body });
  if (error) throw error;
  return data;
}

// ─── Vault list ───────────────────────────────────────────────────────────────

/**
 * Returns all active vaults (direct DB query, no edge function needed).
 */
export async function listVaults() {
  const { data, error } = await supabase.from('vaults').select('*').eq('status', 'active');
  if (error) throw error;
  return data;
}

// ─── Chart data (server-side time-bucket aggregation via PostgreSQL RPC) ─────

/**
 * Fetches chart points via the get_vault_chart PostgreSQL function.
 * Uses date_bin() with fixed bucket sizes per timeframe:
 *   1D → 15min buckets (~96 pts)
 *   1W → 1h   buckets (~168 pts)
 *   1M → 6h   buckets (~120 pts)
 *   ALL→ 1d   buckets
 * Returns { t: epoch_ms, share_price, total_aum }[].
 */
export async function getVaultChartDirect(vaultId, timeframe = '1M') {
  const { data, error } = await supabase.rpc('get_vault_chart', {
    p_vault_id: vaultId,
    p_timeframe: timeframe,
  });
  if (error) throw error;
  return data ?? [];
}

// ─── Vault ────────────────────────────────────────────────────────────────────

/**
 * Returns vault info, config, performance, chart history, and optionally user position.
 * @param {string} vaultId - UUID of the vault
 * @param {string|null} walletAddress - Connected wallet (optional)
 * @param {'1D'|'1W'|'1M'|'ALL'} timeframe
 */
export async function getVaultData(vaultId, walletAddress = null, timeframe = '1M') {
  const data = await invoke('get-vault-data', { vault_id: vaultId, wallet_address: walletAddress, timeframe });
  const v = data?.vaults?.[0];
  if (!v) return null;
  return {
    vault: {
      id: v.id,
      name: v.name,
      address: v.address,
      symbol: v.symbol,
      network: v.network,
      provider: v.provider,
      provider_logo_url: v.provider_logo_url,
      inception_date: v.inception_date,
      description: v.description,
      risk_level: v.risk_level,
      exposure: v.exposure ?? [],
    },
    config: {
      management_fee_pct: v.fees?.management,
      performance_fee_pct: v.fees?.performance,
      entrance_fee_pct: v.fees?.entrance,
      exit_fee_pct: v.fees?.exit,
      high_watermark: v.fees?.high_water_mark,
      timelock_days: v.fees?.timelock_seconds ? Math.round(v.fees.timelock_seconds / 86400) : null,
      min_deposit: v.fees?.min_deposit,
      comptroller_address: v.comptroller_address ?? null,
    },
    perf: {
      share_price: v.share_price,
      total_aum: v.total_aum,
      total_shares: v.total_shares,
      depositors: v.depositors,
      perf_1d_pct: v.perf_1d,
      perf_7d_pct: v.perf_7d,
      perf_30d_pct: v.perf_30d,
    },
    sparkline_7d: v.sparkline_7d ?? [],
    chart_history: v.chart_history ?? [],
    user_position: v.user_position
      ? { ...v.user_position, value_usdc: v.user_position.current_value_usdc }
      : null,
  };
}

// ─── Portfolio ────────────────────────────────────────────────────────────────

/**
 * Returns full portfolio: KPIs, positions, chart, daily yield, transactions, redemptions.
 * @param {string} walletAddress
 * @param {'DASHBOARD'|'1D'|'1W'|'1M'|'ALL'} timeframe
 */
export async function getUserPortfolio(walletAddress, timeframe = 'DASHBOARD') {
  return invoke('GET-USER-PORTFOLIO', { wallet_address: walletAddress, timeframe });
}

// ─── Transactions ─────────────────────────────────────────────────────────────

/**
 * Records a new transaction and updates user position.
 * @param {object} tx
 * @param {string} tx.wallet_address
 * @param {string} tx.vault_id
 * @param {'deposit'|'withdrawal'|'entrance_fee'|'exit_fee'|'management_fee'|'performance_fee'} tx.tx_type
 * @param {number} tx.usdc_amount
 * @param {number} tx.shares_amount
 * @param {string|null} tx.tx_hash
 * @param {'pending'|'confirmed'|'failed'} tx.status
 * @param {'app'|'onchain_sync'} tx.source
 */
export async function createTransaction(tx) {
  return invoke('CREATE-USER-TRANSACTION', tx);
}

// ─── Preferences ──────────────────────────────────────────────────────────────

/**
 * Gets or creates user preferences (upserts on first login).
 * @param {string} walletAddress
 */
export async function getUserPreferences(walletAddress) {
  return invoke('get-user-preferences', { wallet_address: walletAddress });
}

/**
 * Updates allowed preference fields.
 * @param {string} walletAddress
 * @param {object} prefs - Fields: theme, notif_price_alerts, notif_tx_confirmations, notif_weekly_report, newsletter_email
 */
export async function updateUserPreferences(walletAddress, prefs) {
  return invoke('update-user-preferences', { wallet_address: walletAddress, ...prefs });
}

// ─── On-chain sync ────────────────────────────────────────────────────────────

/**
 * Reads on-chain share balances and syncs user_positions in DB.
 * Called on wallet connect to catch positions created directly on Enzyme.
 * @param {string} walletAddress
 */
export async function syncOnChainPositions(walletAddress) {
  return invoke('sync-onchain-positions', { wallet_address: walletAddress });
}

// ─── Redemptions ──────────────────────────────────────────────────────────────

/**
 * Lists the user's own pending redemptions.
 * @param {string} walletAddress
 */
export async function listUserRedemptions(walletAddress) {
  return invoke('REDEMPTION_API', { action: 'list_user', wallet_address: walletAddress });
}

/**
 * Marks a redemption as completed after the on-chain tx.
 * @param {string} walletAddress
 * @param {string} redemptionId
 * @param {string|null} txHash
 */
export async function completeRedemption(walletAddress, redemptionId, txHash = null) {
  return invoke('REDEMPTION_API', { action: 'complete', wallet_address: walletAddress, redemption_id: redemptionId, tx_hash: txHash });
}

/**
 * Lists pending redemptions + liquidity forecast (internal G12 use).
 * @param {string} walletAddress
 * @param {number} daysAhead - Days to forecast (default 14)
 */
export async function listRedemptions(walletAddress, daysAhead = 14) {
  return invoke('REDEMPTION_API', { action: 'list', wallet_address: walletAddress, days_ahead: daysAhead });
}

/**
 * Creates a redemption request (72h timelock).
 * @param {string} walletAddress
 * @param {string} vaultId
 * @param {number} sharesAmount
 */
export async function createRedemption(walletAddress, vaultId, sharesAmount) {
  return invoke('REDEMPTION_API', {
    action: 'create',
    wallet_address: walletAddress,
    vault_id: vaultId,
    shares_amount: sharesAmount,
  });
}

/**
 * Cancels a pending redemption request.
 * @param {string} walletAddress
 * @param {string} redemptionId
 */
export async function cancelRedemption(walletAddress, redemptionId) {
  return invoke('REDEMPTION_API', {
    action: 'cancel',
    wallet_address: walletAddress,
    redemption_id: redemptionId,
  });
}

// ─── Notifications ───────────────────────────────────────────────────────────

/**
 * Fetches notifications for a wallet.
 * @param {string} walletAddress
 * @param {number} limit
 * @param {number} offset
 */
export async function getNotifications(walletAddress, limit = 20, offset = 0) {
  return invoke('get-notifications', { wallet_address: walletAddress, limit, offset });
}

/**
 * Marks notifications as read.
 * @param {string} walletAddress
 * @param {string[]|null} ids - Specific IDs, or null to mark all as read
 */
export async function markNotificationsRead(walletAddress, ids = null) {
  return invoke('mark-notifications-read', { wallet_address: walletAddress, ids });
}
